/**
 * Outil « Des intérêts composés à e » — calculs (pur).
 *
 * 1 € placé à 100 % par an, les intérêts versés n fois dans l'année au taux
 * 1/n : au bout d'un an, uₙ = (1 + 1/n)ⁿ euros. uₙ croît vers e ≈ 2,718 28.
 * Calcul en exp(n · ln(1 + 1/n)) (log1p) : précis même pour n = 10⁶.
 */

export const E_DIGITS = '2,718281828459';

export function compound(n: number): number {
  return Math.exp(n * Math.log1p(1 / n));
}

export const STEPS: ReadonlyArray<{ n: number; label: string }> = [
  { n: 1, label: 'une fois par an' },
  { n: 2, label: 'par semestre' },
  { n: 4, label: 'par trimestre' },
  { n: 12, label: 'par mois' },
  { n: 52, label: 'par semaine' },
  { n: 365, label: 'par jour' },
  { n: 8760, label: 'par heure' },
  { n: 525600, label: 'par minute' },
];

/** curseur logarithmique : t ∈ [0 ; 6] ↦ n = 10ᵗ arrondi */
export function nOfSlider(t: number): number {
  return Math.max(1, Math.round(10 ** t));
}

/** valeur écrite à la française avec `d` décimales TRONQUÉES (jamais arrondies) */
export function truncText(v: number, d: number): string {
  const s = (Math.floor(v * 10 ** d) / 10 ** d).toFixed(d);
  return s.replace('.', ',');
}

/**
 * uₙ écrit EXACTEMENT quand il a au plus 9 décimales (n = 1, 2, 4, 5 :
 * 2 ; 2,25 ; 2,441 406 25 ; 2,488 32), sinon null. Calcul en entiers.
 */
export function exactText(n: number): string | null {
  let m = n;
  let twos = 0;
  let fives = 0;
  while (m % 2 === 0) {
    m /= 2;
    twos++;
  }
  while (m % 5 === 0) {
    m /= 5;
    fives++;
  }
  const d = Math.max(twos, fives) * n;
  if (m !== 1 || d > 9) return null;
  const q = (
    (BigInt(n + 1) ** BigInt(n) * BigInt(10) ** BigInt(d)) /
    BigInt(n) ** BigInt(n)
  ).toString();
  const int = q.slice(0, q.length - d);
  const frac = q.slice(q.length - d).replace(/0+$/, '');
  return frac ? `${int},${frac}` : int;
}

/** nombre de décimales de uₙ qui coïncident avec celles de e (troncatures) */
export function correctDecimals(v: number): number {
  let k = 0;
  while (k < 10 && truncText(v, k + 1) === truncText(Math.E, k + 1)) k++;
  return k;
}

/** e^(kx) et sa pente en x₀ : f′(x₀) = k · f(x₀) */
export function expAt(k: number, x: number): { y: number; slope: number } {
  const y = Math.exp(k * x);
  return { y, slope: k * y };
}

/** temps de doublement ln 2 / k (k > 0) */
export function doublingTime(k: number): number | null {
  return k > 0 ? Math.LN2 / k : null;
}
